import { useState, useEffect, useCallback } from 'react';

interface VersionCheckResult {
  hasUpdate: boolean;
  currentVersion: string;
  latestVersion: string;
  checkForUpdates: () => Promise<void>;
  handleUpdate: () => void;
  dismissUpdate: () => void;
}

export const useVersionCheck = (): VersionCheckResult => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [latestVersion, setLatestVersion] = useState<string>('');
  const [isDismissed, setIsDismissed] = useState(false);

  const checkForUpdates = useCallback(async () => {
    try {
      // Mevcut version'ı localStorage'dan al
      const storedVersion = localStorage.getItem('app_version');
      setCurrentVersion(storedVersion || 'unknown');

      // Server'dan latest version'ı al (sessiz hata yönetimi)
      const response = await fetch('/version.json?t=' + new Date().getTime());
      if (response.ok) {
        const versionData = await response.json();
        const latestVersionHash = versionData.hash || 'unknown';
        setLatestVersion(latestVersionHash);

        // Eğer version farklıysa ve dismiss edilmemişse update alert göster
        if (storedVersion && storedVersion !== latestVersionHash && !isDismissed) {
          setHasUpdate(true);
        } else if (!storedVersion) {
          // İlk kez giriş yapıyorsa version'ı kaydet
          localStorage.setItem('app_version', latestVersionHash);
          setCurrentVersion(latestVersionHash);
        }
      }
      // response.ok değilse sessiz şekilde devam et (404 veya diğer hatalar ihmal edilir)
    } catch (error) {
      // Network hatalarını sessiz şekilde yönet (console'a log yapma)
      // Version check başarısız olsa bile uygulama normal şekilde çalışmaya devam eder
    }
  }, [isDismissed]);

  const handleUpdate = useCallback(() => {
    // Cache'i temizle ve sayfayı yenile
    localStorage.removeItem('app_version');
    localStorage.removeItem('cached_data');
    sessionStorage.clear();
    
    // Hard reload
    window.location.reload();
  }, []);

  const dismissUpdate = useCallback(() => {
    setHasUpdate(false);
    setIsDismissed(true);
    // 5 dakika sonra tekrar kontrol et
    setTimeout(() => {
      setIsDismissed(false);
    }, 5 * 60 * 1000);
  }, []);

  // Component mount olduğunda ve sayfa focus olduğunda kontrol et
  useEffect(() => {
    checkForUpdates();

    // Sayfa focus olduğunda da kontrol et
    const handleFocus = () => {
      checkForUpdates();
    };

    // Visibility change olduğunda da kontrol et
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates]);

  return {
    hasUpdate,
    currentVersion,
    latestVersion,
    checkForUpdates,
    handleUpdate,
    dismissUpdate
  };
};

